import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  IAddPageConfiguration,
  IConfigurationFormDropdown,
  IFormDropdownResponse,
  IGetFormEntriesAndUserDefinedFieldsData,
  IGetFormEntriesAndUserDefinedFieldsPayload,
  ILogbookAndFormDropdownsResponse,
  ILogbookDropdownResponse,
  ILogsFormEntriesColumn,
  IPageConfiguration,
  IPageConfigurationParams,
  IUserDefineFields,
} from './page-configuration.model';
import { forkJoin, map, Observable } from 'rxjs';
import { IBaseOneResponse, IGetManyResponse } from '../../shared/model/interface/crud-response-interface.model';
import {
  IClientPageConfiguration,
  IClientPageDeleteConfiguration,
} from '../../view/settings/logs-table-configuration/home-logs-table-configuration/home-logs-table-configuration.model';
import { ServiceUtilities } from '../../shared/helper/service-utilities';
import {
  IReportPageConfiguration,
  IReportPageDeleteConfiguration
} from '../../view/settings/logs-table-configuration/report-logs-table-configuration/report-logs-table-configuration.model';
import _ from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class PageConfigurationService {
  private readonly URLS = {
    LOAD_USER_HOME_PAGE_CONFIGURATION: `${this.baseUrl}/configurations/home/`,
    CLIENT_CONFIGURATION: `${this.baseUrl}/configurations/client/`,
    ADD_USER_CONFIGURATION: `${this.baseUrl}/configurations/add-user/`,
    ACTIVE_FORM_VERSIONS: `${this.baseUrl}/form-versions/actives`,
    USER_FORM_SETTINGS_PAGE_CONFIGURATION: `${this.baseUrl}/configurations/forms/`,
    USER_LOGBOOK_SETTINGS_PAGE_CONFIGURATION: `${this.baseUrl}/configurations/logbooks/`,
    USER_ALL_LOGBOOK_VERSION_SETTINGS_PAGE_CONFIGURATION: `${this.baseUrl}/configurations/all-logbook-versions/`,
    REPORT_CONFIGURATION: `${this.baseUrl}/configurations/report/`,
    FORM_VERSION_COLUMNS: `${this.baseUrl}/form-versions/form-version-fields`,
    USER_DEFINED_FIELDS: `${this.baseUrl}/field-versions/field-versions-by-forms-and-logbooks`,
    LOGBOOKS: `${this.baseUrl}/logbooks/versions-form-assignments`,
    FORMS: `${this.baseUrl}/forms/versions-logbook-assignments`,
  };

  constructor(public http: HttpClient, @Inject('API_BASE_URL') private readonly baseUrl: string) {}

  public loadUserHomePageConfiguration(
    params: IPageConfigurationParams,
  ): Observable<IBaseOneResponse<IPageConfiguration>> {
    return this.http.get<IBaseOneResponse<IPageConfiguration>>(
      `${this.URLS.LOAD_USER_HOME_PAGE_CONFIGURATION}?user_id=${params.userId}&logbook_ids=[${params.logbookId}]`,
    );
  }

  public addUserHomePageConfiguration(
    request: IAddPageConfiguration,
  ): Observable<IBaseOneResponse<IPageConfiguration>> {
    return this.http.post<IBaseOneResponse<IPageConfiguration>>(this.URLS.ADD_USER_CONFIGURATION, request);
  }

  public updateUserHomePageConfiguration(
    request: IAddPageConfiguration,
  ): Observable<IBaseOneResponse<IPageConfiguration>> {
    return this.http.put<IBaseOneResponse<IPageConfiguration>>(this.URLS.ADD_USER_CONFIGURATION, request);
  }

  public loadClientHomeConfiguration(): Observable<IGetManyResponse<IClientPageConfiguration>> {
    return this.http.get<IGetManyResponse<IClientPageConfiguration>>(`${this.URLS.CLIENT_CONFIGURATION}`);
  }

  public loadReportConfiguration(): Observable<IGetManyResponse<IReportPageConfiguration>> {
    return this.http.get<IGetManyResponse<IReportPageConfiguration>>(`${this.URLS.REPORT_CONFIGURATION}`);
  }

  public deleteClientHomeConfiguration(
    request: IClientPageDeleteConfiguration,
  ): Observable<IGetManyResponse<IClientPageConfiguration>> {
    return this.http.delete<IGetManyResponse<IClientPageConfiguration>>(this.URLS.CLIENT_CONFIGURATION, {
      body: {
        issuer: request.issuer,
        reason: request.reason,
        payload: request,
      },
    });
  }

  public loadSelectedLogbookForms(
    formIds: number[],
    parentLogbookIds: number[],
  ): Observable<IGetManyResponse<IConfigurationFormDropdown>> {
    const body: Record<string, string | number> = ServiceUtilities.prepareGenericBodyForRequest({
      form_ids: JSON.stringify(formIds),
      parent_logbook_ids: JSON.stringify(parentLogbookIds),
    });

    return this.http.post<IGetManyResponse<IConfigurationFormDropdown>>(
      `${this.URLS.ACTIVE_FORM_VERSIONS}`,
      { ...body },
      {
        headers: new HttpHeaders({ 'X-HTTP-Method': 'GET' }),
      },
    );
  }

  public addClientPageConfiguration(
    request: IAddPageConfiguration,
  ): Observable<IGetManyResponse<IClientPageConfiguration>> {
    return this.http.post<IGetManyResponse<IClientPageConfiguration>>(this.URLS.CLIENT_CONFIGURATION, {
      issuer: request.issuer,
      reason: request.reason,
      payload: request.payload,
    });
  }

  public addReportConfiguration(
    request: IAddPageConfiguration,
  ): Observable<IGetManyResponse<IReportPageConfiguration>> {
    return this.http.post<IGetManyResponse<IReportPageConfiguration>>(this.URLS.REPORT_CONFIGURATION, {
      issuer: request.issuer,
      reason: request.reason,
      payload: request.payload,
    });
  }

  public editClientPageConfiguration(
    request: IAddPageConfiguration,
  ): Observable<IGetManyResponse<IClientPageConfiguration>> {
    return this.http.put<IGetManyResponse<IClientPageConfiguration>>(this.URLS.CLIENT_CONFIGURATION, {
      issuer: request.issuer,
      reason: request.reason,
      payload: request.payload,
    });
  }

  public editReportPageConfiguration(
    pageConfigurationId: number,
    request: IAddPageConfiguration,
  ): Observable<IGetManyResponse<IReportPageConfiguration>> {
    const url = `${this.URLS.REPORT_CONFIGURATION}${pageConfigurationId}`;
    return this.http.put<IGetManyResponse<IReportPageConfiguration>>(url, {
      issuer: request.issuer,
      reason: request.reason,
      payload: request.payload,
    });
  }

  public deleteReportPageConfiguration(
    pageConfigurationId: number,
    request: IReportPageDeleteConfiguration,
  ): Observable<IGetManyResponse<IReportPageConfiguration>> {
    const url = `${this.URLS.REPORT_CONFIGURATION}${pageConfigurationId}`;
    return this.http.delete<IGetManyResponse<IReportPageConfiguration>>(url, {
      body: {
        issuer: request.issuer,
        reason: request.reason,
        payload: request,
      },
    });
  }

  public loadUserFormSettingsPageConfiguration(userId: number): Observable<IBaseOneResponse<IPageConfiguration>> {
    return this.http.get<IBaseOneResponse<IPageConfiguration>>(
      `${this.URLS.USER_FORM_SETTINGS_PAGE_CONFIGURATION}?user_id=${userId}`,
    );
  }

  public loadUserLogbookSettingsPageConfiguration(userId: number): Observable<IBaseOneResponse<IPageConfiguration>> {
    return this.http.get<IBaseOneResponse<IPageConfiguration>>(
      `${this.URLS.USER_LOGBOOK_SETTINGS_PAGE_CONFIGURATION}?user_id=${userId}`,
    );
  }

  public loadUserAllLogbookVersionSettingsPageConfiguration(
    userId: number,
  ): Observable<IBaseOneResponse<IPageConfiguration>> {
    return this.http.get<IBaseOneResponse<IPageConfiguration>>(
      `${this.URLS.USER_ALL_LOGBOOK_VERSION_SETTINGS_PAGE_CONFIGURATION}?user_id=${userId}`,
    );
  }

  public addUserFormSettingsPageConfiguration(
    request: IAddPageConfiguration,
    isUpdate: boolean,
  ): Observable<IBaseOneResponse<IPageConfiguration>> {
    if (isUpdate) {
      return this.http.put<IBaseOneResponse<IPageConfiguration>>(
        this.URLS.USER_FORM_SETTINGS_PAGE_CONFIGURATION,
        request,
      );
    }

    return this.http.post<IBaseOneResponse<IPageConfiguration>>(
      this.URLS.USER_FORM_SETTINGS_PAGE_CONFIGURATION,
      request,
    );
  }

  public addUserLogbookSettingsPageConfiguration(
    request: IAddPageConfiguration,
    isUpdate: boolean,
  ): Observable<IBaseOneResponse<IPageConfiguration>> {
    if (isUpdate) {
      return this.http.put<IBaseOneResponse<IPageConfiguration>>(
        this.URLS.USER_LOGBOOK_SETTINGS_PAGE_CONFIGURATION,
        request,
      );
    }

    return this.http.post<IBaseOneResponse<IPageConfiguration>>(
      this.URLS.USER_LOGBOOK_SETTINGS_PAGE_CONFIGURATION,
      request,
    );
  }

  public addUserALLLogbookVersionSettingsPageConfiguration(
    request: IAddPageConfiguration,
    isUpdate: boolean,
  ): Observable<IBaseOneResponse<IPageConfiguration>> {
    if (isUpdate) {
      return this.http.put<IBaseOneResponse<IPageConfiguration>>(
        this.URLS.USER_ALL_LOGBOOK_VERSION_SETTINGS_PAGE_CONFIGURATION,
        request,
      );
    }

    return this.http.post<IBaseOneResponse<IPageConfiguration>>(
      this.URLS.USER_ALL_LOGBOOK_VERSION_SETTINGS_PAGE_CONFIGURATION,
      request,
    );
  }

  public getFormEntriesColumns(payload: IGetFormEntriesAndUserDefinedFieldsPayload) {
    return this.http.post<IGetManyResponse<ILogsFormEntriesColumn>>(this.URLS.FORM_VERSION_COLUMNS, {
      payload: { formIds: payload.formIds },
    });
  }

  public getUserDefinedFields(payload: IGetFormEntriesAndUserDefinedFieldsPayload) {
    return this.http.post<IGetManyResponse<IUserDefineFields>>(this.URLS.USER_DEFINED_FIELDS, {
      payload: { forms: payload.formIds, logbooks: payload.logbookIds },
    });
  }

  public getFormEntriesAndUserDefinedFields(
    payload: IGetFormEntriesAndUserDefinedFieldsPayload,
  ): Observable<IGetFormEntriesAndUserDefinedFieldsData> {
    return forkJoin([this.getFormEntriesColumns(payload), this.getUserDefinedFields(payload)]).pipe(
      map((responseList) => {
        return {
          formEntriesColumns: _.get(responseList, '0.data', []),
          logbookUserDefineFields: _.get(responseList, '1.data.logbooks', []),
          formUserDefineFields: _.get(responseList, '1.data.forms', []),
          formSelectableFields: _.get(responseList, '1.data.formSelectableFields', []),
        };
      }),
    );
  }

  public getLogbookDropdownData() {
    return this.http.get<IGetManyResponse<ILogbookDropdownResponse>>(this.URLS.LOGBOOKS);
  }

  public getFormDropdownData() {
    return this.http.get<IGetManyResponse<IFormDropdownResponse>>(this.URLS.FORMS);
  }

  public getLogbookAndFormDropdowns(): Observable<ILogbookAndFormDropdownsResponse> {
    return forkJoin([this.getLogbookDropdownData(), this.getFormDropdownData()]).pipe(
      map((responseList) => {
        return {
          logbookDropdownData: _.get(responseList, '0.data', []),
          formDropdownData: _.get(responseList, '1.data', []),
        };
      }),
    );
  }
}
